import { useEffect, useState } from 'react';
import { Button, Sheet, sheetText } from '../components/ui';
import { Icon } from '../components/Icon';
import { useApp } from '../lib/app';
import { clock, rupees } from '../lib/format';
import { useNow } from '../lib/eta';
import { useT } from '../lib/i18n';
import { cancelTerms } from '../lib/rideScreen';
import type { StringKey } from '../lib/strings';
import type { Ride } from '../lib/types';

/** Reasons the rider can give (sent as the cancel `reason`, in English for the team). */
const REASONS_RIDE = ['wait', 'plans', 'pickup', 'driver', 'mistake', 'other'] as const;
const REASONS_REQUEST = ['plans', 'mistake', 'pickup', 'other'] as const;
const EN_REASON: Record<string, string> = {
  wait: 'The driver is taking too long',
  plans: 'My plans changed',
  pickup: 'Wrong pickup spot',
  driver: 'The driver asked me to cancel',
  mistake: 'I booked by mistake',
  other: 'Something else',
};

/**
 * Cancel, Uber-style: why, and what it costs — the free-cancel window comes from the server's settings
 * (`cancellationGraceSeconds` after the driver accepted, `cancellationFeePaise` after it). Searching for a driver
 * is always free.
 */
export function CancelSheet({ open, ride, onClose, onConfirm }: { open: boolean; ride: Ride; onClose: () => void; onConfirm: (reason: string) => void }) {
  const { settings } = useApp();
  const t = useT();
  const now = useNow(5_000);
  const searching = ride.status === 'REQUESTED' || ride.status === 'OFFERING';
  const reasons = searching ? REASONS_REQUEST : REASONS_RIDE;
  const [pick, setPick] = useState<string | null>(null);
  useEffect(() => {
    if (open) setPick(null);
  }, [open]);
  const terms = cancelTerms(ride, settings, now);
  const cost = terms.free ? (terms.until ? t('cancel.freeUntil', { time: clock(terms.until), fee: rupees(terms.feePaise) }) : t('cancel.free')) : t('cancel.fee', { fee: rupees(terms.feePaise) });
  return (
    <Sheet open={open} onClose={onClose} title={searching ? t('cancel.titleRequest') : t('cancel.titleRide')}>
      <p data-testid="cancel-terms" style={{ ...sheetText, color: terms.free ? 'var(--ink2)' : 'var(--danger)' }}>
        {cost}
      </p>
      <p style={{ margin: '16px 0 0', fontSize: 13, lineHeight: '18px', fontWeight: 700, color: 'var(--ink3)', letterSpacing: '0.04em' }}>{t('cancel.why')}</p>
      <div role="radiogroup" aria-label={t('cancel.why')} style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {reasons.map((r) => {
          const on = pick === r;
          return (
            <button
              key={r}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => setPick(r)}
              className="zr-press-soft"
              style={{ minHeight: 48, border: 0, borderRadius: 16, background: on ? 'var(--acSoft)' : 'var(--s1)', boxShadow: on ? 'inset 0 0 0 1.5px var(--sel)' : 'inset 0 0 0 1.5px transparent', padding: '0 16px', display: 'flex', alignItems: 'center', gap: 12, color: 'var(--ink)', textAlign: 'left', fontSize: 15, fontWeight: 700 }}
            >
              <span style={{ flex: 1 }}>{t(`cancel.r.${r}` as StringKey)}</span>
              {on && <Icon name="check" size={18} sw={2.6} style={{ color: 'var(--acText)' }} />}
            </button>
          );
        })}
      </div>
      <div style={{ marginTop: 18, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
        <Button variant="secondary" onClick={onClose}>
          {searching ? t('cancel.keepRequest') : t('cancel.keepRide')}
        </Button>
        <Button variant="secondary" disabled={!pick} onClick={() => pick && onConfirm(EN_REASON[pick])} style={{ color: 'var(--danger)' }}>
          {t('cancel.yes')}
        </Button>
      </div>
    </Sheet>
  );
}
